import { useState } from 'react';
import { Modal } from '../ui/Modal.jsx';
import { rentalsApi } from '../../api/rentals.js';

const todayIso = () => new Date().toISOString().slice(0, 10);

export function RentalForm({ open, onClose, carId, carKm, onCreated }) {
  const [chiriasNume, setChiriasNume] = useState('');
  const [chiriasTelefon, setChiriasTelefon] = useState('');
  const [dataStart, setDataStart] = useState(todayIso());
  const [dataEnd, setDataEnd] = useState('');
  const [kmStart, setKmStart] = useState(carKm ?? '');
  const [costContract, setCostContract] = useState('');
  const [garantie, setGarantie] = useState('');
  const [avansPrimit, setAvansPrimit] = useState('');
  const [observatii, setObservatii] = useState('');
  const [idFile, setIdFile] = useState(null);
  const [permisFile, setPermisFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function reset() {
    setChiriasNume('');
    setChiriasTelefon('');
    setDataStart(todayIso());
    setDataEnd('');
    setKmStart(carKm ?? '');
    setCostContract('');
    setGarantie('');
    setAvansPrimit('');
    setObservatii('');
    setIdFile(null);
    setPermisFile(null);
    setErr('');
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('car_id', carId);
      fd.append('chirias_nume', chiriasNume);
      if (chiriasTelefon) fd.append('chirias_telefon', chiriasTelefon);
      fd.append('data_start', dataStart);
      if (dataEnd) fd.append('data_end', dataEnd);
      if (kmStart !== '' && kmStart != null) fd.append('km_start', String(kmStart));
      if (costContract !== '') fd.append('cost_contract', String(costContract));
      if (garantie !== '') fd.append('garantie', String(garantie));
      if (avansPrimit !== '') fd.append('avans_primit', String(avansPrimit));
      if (observatii) fd.append('observatii', observatii);
      if (idFile) fd.append('id_doc', idFile);
      if (permisFile) fd.append('permis_doc', permisFile);

      const res = await rentalsApi.create(fd);
      onCreated?.(res.rental);
      reset();
      onClose?.();
    } catch (ex) {
      console.error('[RentalForm] create failed:', ex, 'body:', ex.body);
      if (ex.body?.error === 'invalid_mime') setErr('Fișier invalid — doar PDF, JPEG sau PNG.');
      else if (ex.body?.error === 'file_too_large') setErr('Fișier peste 10MB.');
      else if (ex.body?.error === 'validation_error') {
        const issues = ex.body?.issues || [];
        const fields = issues.map((i) => i.path?.join('.')).filter(Boolean).join(', ');
        setErr(`Date invalide${fields ? ' la: ' + fields : '.'} — verifică toate câmpurile.`);
      }
      else if (ex.body?.error === 'car_id_required') setErr('Mașină neidentificată. Închide modalul și redeschide-l.');
      else if (ex.body?.error === 'car_not_found') setErr('Mașina nu e accesibilă contului tău.');
      else if (ex.status === 401) setErr('Sesiunea a expirat. Reloghează-te.');
      else setErr(`Eroare (${ex.status || '?'}): ${ex.body?.error || ex.message || 'necunoscută'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Închiriere nouă">
      <form onSubmit={onSubmit} className="space-y-4">
        {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Nume chiriaș *</span>
            <input
              required
              value={chiriasNume}
              onChange={(e) => setChiriasNume(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block col-span-2 sm:col-span-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Telefon</span>
            <input
              type="tel"
              value={chiriasTelefon}
              onChange={(e) => setChiriasTelefon(e.target.value)}
              placeholder="+40 7XX XXX XXX"
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Data start *</span>
            <input
              type="date"
              required
              value={dataStart}
              onChange={(e) => setDataStart(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Data end</span>
            <input
              type="date"
              value={dataEnd}
              onChange={(e) => setDataEnd(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">KM start</span>
            <input
              type="number"
              min={0}
              value={kmStart}
              onChange={(e) => setKmStart(e.target.value)}
              placeholder={carKm ? `auto ${carKm}` : 'km'}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
        </div>

        <div className="rounded-lg border border-border bg-cream/50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Contract (RON)</div>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Cost</span>
              <input
                type="number" min={0} placeholder="0"
                value={costContract}
                onChange={(e) => setCostContract(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Garanție</span>
              <input
                type="number" min={0} placeholder="0"
                value={garantie}
                onChange={(e) => setGarantie(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Avans primit</span>
              <input
                type="number" min={0} placeholder="0"
                value={avansPrimit}
                onChange={(e) => setAvansPrimit(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-white px-2 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
              />
            </label>
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Observații</span>
          <textarea
            rows={2}
            value={observatii}
            onChange={(e) => setObservatii(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Copie ID</span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setIdFile(e.target.files?.[0] || null)}
              className="mt-1 block text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Copie permis</span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setPermisFile(e.target.files?.[0] || null)}
              className="mt-1 block text-sm"
            />
          </label>
        </div>
        <p className="text-xs text-muted">PDF/JPEG/PNG, max 10MB fiecare.</p>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => { reset(); onClose?.(); }} className="btn-ghost">Anulează</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Se salvează…' : 'Înregistrează'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
